import { useMemo, useState } from 'react';
import { CHROME } from './palette';
import { formatMkd } from '../../lib/format';

/**
 * Grouped bars — one group per month, one bar per business. Replaces the
 * LiveCharts ColumnSeries on the desktop comparison screens.
 *
 * Bars carry a 2px surface gap so adjacent fills never touch, and rounded
 * data-ends anchored to the baseline.
 */

export interface BarSeries {
  key: string;
  label: string;
  color: string;
  values: number[];
}

const PAD = { top: 16, right: 16, bottom: 34, left: 68 };
const HEIGHT = 260;
const WIDTH = 720;
const BAR_GAP = 2;

export default function BarChart({
  labels,
  series,
  ariaLabel,
}: {
  labels: string[];
  series: BarSeries[];
  ariaLabel: string;
}) {
  const [hover, setHover] = useState<number | null>(null);

  const { max, ticks } = useMemo(() => {
    const rawMax = Math.max(1, ...series.flatMap((s) => s.values));
    const magnitude = 10 ** Math.floor(Math.log10(rawMax));
    const niceMax = Math.ceil(rawMax / magnitude) * magnitude;
    return { max: niceMax, ticks: [0, 0.25, 0.5, 0.75, 1].map((f) => niceMax * f) };
  }, [series]);

  if (labels.length === 0) {
    return (
      <div className="flex h-52 items-center justify-center text-sm text-muted">
        Nuk ka të dhëna për këtë periudhë.
      </div>
    );
  }

  const plotW = WIDTH - PAD.left - PAD.right;
  const plotH = HEIGHT - PAD.top - PAD.bottom;
  const groupW = plotW / labels.length;
  const barW = Math.max(3, (groupW * 0.72) / series.length - BAR_GAP);
  const y = (v: number) => PAD.top + plotH - (v / max) * plotH;
  const labelStep = Math.max(1, Math.ceil(labels.length / 8));

  return (
    <div>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="w-full"
        role="img"
        aria-label={ariaLabel}
        onMouseLeave={() => setHover(null)}
      >
        {ticks.map((t) => (
          <g key={t}>
            <line
              x1={PAD.left}
              x2={WIDTH - PAD.right}
              y1={y(t)}
              y2={y(t)}
              stroke={CHROME.grid}
              strokeWidth="1"
            />
            <text
              x={PAD.left - 8}
              y={y(t) + 4}
              textAnchor="end"
              fontSize="11"
              fill={CHROME.muted}
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {Math.round(t).toLocaleString('sq-MK')}
            </text>
          </g>
        ))}

        <line
          x1={PAD.left}
          x2={WIDTH - PAD.right}
          y1={y(0)}
          y2={y(0)}
          stroke={CHROME.axis}
          strokeWidth="1"
        />

        {labels.map((label, i) => {
          const groupX = PAD.left + i * groupW;
          const groupInner = series.length * (barW + BAR_GAP);
          const startX = groupX + (groupW - groupInner) / 2;

          return (
            <g key={label + i}>
              {hover === i && (
                <rect
                  x={groupX}
                  y={PAD.top}
                  width={groupW}
                  height={plotH}
                  fill={CHROME.grid}
                  opacity="0.35"
                />
              )}

              {series.map((s, si) => {
                const value = s.values[i] ?? 0;
                const barH = Math.max(0, y(0) - y(value));
                return (
                  <rect
                    key={s.key}
                    x={startX + si * (barW + BAR_GAP)}
                    y={y(value)}
                    width={barW}
                    height={barH}
                    rx={Math.min(4, barW / 2)}
                    fill={s.color}
                  />
                );
              })}

              {i % labelStep === 0 && (
                <text
                  x={groupX + groupW / 2}
                  y={HEIGHT - 12}
                  textAnchor="middle"
                  fontSize="11"
                  fill={CHROME.muted}
                >
                  {label}
                </text>
              )}

              <rect
                x={groupX}
                y={PAD.top}
                width={groupW}
                height={plotH}
                fill="transparent"
                onMouseEnter={() => setHover(i)}
              />
            </g>
          );
        })}
      </svg>

      <div className="mt-2 flex flex-wrap items-center gap-4">
        {series.map((s) => (
          <span key={s.key} className="flex items-center gap-1.5 text-xs text-muted">
            <span
              className="inline-block h-2.5 w-2.5 rounded-sm"
              style={{ background: s.color }}
              aria-hidden="true"
            />
            {s.label}
          </span>
        ))}
      </div>

      {hover !== null && (
        <div className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-sm">
          <span className="font-medium">{labels[hover]}</span>
          {series.map((s) => (
            <span key={s.key} className="ml-4 text-muted">
              {s.label}:{' '}
              <span className="tnum font-medium text-ink">{formatMkd(s.values[hover] ?? 0)}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
